// Partner tracking links (UTM) — captures where a visitor came from so the
// sign-up can be credited to the right marketing partner. Backend side and
// the rules: backend/services/acquisition.py, docs/TRACKING_LINKS.md.
//
// Consent (PECR): remembering a visitor's source across visits is marketing
// measurement, so it is only written to localStorage after the visitor has
// accepted ANALYTICS cookies. Without that consent the tags live in memory
// for the current visit only — enough for someone who clicks a link and signs
// up straight away, and nothing is stored on their device.
//
// First touch wins: once a source is held, a later link in the same window
// doesn't replace it (the backend applies the same rule per account).

import { getStoredConsent } from '../components/CookieConsentBanner';

const STORAGE_KEY = 'ariv_acquisition';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'];

let inMemory = null;

const hasAnalyticsConsent = () => {
  try {
    return getStoredConsent()?.analytics === true;
  } catch {
    return false;
  }
};

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.source || !parsed?.capturedAt) return null;
    if (Date.now() - parsed.capturedAt > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeStored = (record) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* storage unavailable (private mode etc.) — memory copy still works */
  }
};

const clearStored = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/** Read utm_* from the current URL. Returns null if there's no utm_source. */
const fromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    if (!source) return null;
    const record = { capturedAt: Date.now(), landingPath: window.location.pathname };
    UTM_KEYS.forEach((key) => {
      const value = params.get(`utm_${key}`);
      if (value) record[key] = value.slice(0, 64);
    });
    return record;
  } catch {
    return null;
  }
};

/** Call once on app load. Picks up utm_* tags from the landing URL. */
export const captureAcquisition = () => {
  const existing = inMemory || readStored();
  if (existing) {
    inMemory = existing;
    return;
  }
  const fresh = fromUrl();
  if (!fresh) return;
  inMemory = fresh;
  if (hasAnalyticsConsent()) writeStored(fresh);
};

/**
 * Keep storage in line with the visitor's cookie choice: persist the held
 * source when analytics is accepted, delete it when analytics is withdrawn.
 */
export const syncAcquisitionWithConsent = (prefs) => {
  if (prefs?.analytics === true) {
    if (inMemory && !readStored()) writeStored(inMemory);
  } else {
    clearStored();
  }
};

/** The payload sent with a sign-up as `acquisition`, or undefined if none. */
export const getAcquisition = () => {
  const record = inMemory || readStored();
  if (!record?.source) return undefined;
  const payload = { landingPath: record.landingPath };
  UTM_KEYS.forEach((key) => {
    if (record[key]) payload[key] = record[key];
  });
  return payload;
};

/** Build a tracking link (used by the admin link builder). */
export const buildTrackingLink = (baseUrl, { source, medium, campaign, content } = {}) => {
  const url = new URL(baseUrl);
  const tag = (v) => (v || '').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9._+-]/g, '');
  if (tag(source)) url.searchParams.set('utm_source', tag(source));
  if (tag(medium)) url.searchParams.set('utm_medium', tag(medium));
  if (tag(campaign)) url.searchParams.set('utm_campaign', tag(campaign));
  if (tag(content)) url.searchParams.set('utm_content', tag(content));
  return url.toString();
};
